import { useContext, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import CallTakerServices from "@/CADServices/APIs/callTaker";
import { useQuery } from 'react-query';
import { IncidentContext } from '@/CADContext/Incident';
import CallOpenInsightCard from '@/CADComponents/CallTakerModal/CallOpenInsightCard';
import CfsProtocolInsightCard from '@/CADComponents/CallTakerModal/CfsProtocolInsightCard';
import { getShowingDateText } from '@/Components/Common/Utility';

const insightMono = { fontFamily: 'ui-monospace, Menlo, Consolas, monospace' };

const insightCard = {
  borderRadius: 8,
  padding: '10px 12px',
  fontSize: 11,
  lineHeight: 1.35,
};

const insightSectionTitle = {
  fontSize: 10,
  fontWeight: 800,
  letterSpacing: '0.08em',
  textTransform: 'uppercase',
  marginBottom: 8,
  display: 'flex',
  alignItems: 'center',
  gap: 6,
  color: '#334155',
};

const disciplineFlagTrue = (v) => v === true || v === 1;

function statusToUnitTone(status) {
  const s = String(status ?? '').trim().toUpperCase();
  if (s === 'AV') return 'av';
  if (s === 'BS' || s === 'BUSY' || s === 'ENRT' || s === 'ENR') return 'busy';
  return 'unav';
}

/**
 * Parse API date strings for **calendar-day** math vs today (local).
 * - `2026-03-17` → local midnight that civil date.
 * - `2026-03-17T06:16:54` (no `Z` / offset) → civil date 2026-03-17 local (CAD-style “reported date”).
 * - With explicit zone (`…Z`, `…+05:30`) → `Date` parse, then local calendar day of that instant.
 */
function parseReportedDateLocal(value) {
  if (value == null || value === '') return null;
  const s = String(value).trim();
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) {
    const [y, m, d] = s.split('-').map(Number);
    const dt = new Date(y, m - 1, d);
    return Number.isNaN(dt.getTime()) ? null : dt;
  }
  const hasExplicitZone = /Z$/i.test(s) || /[+-]\d{2}:?\d{2}$/.test(s);
  const civilFromIsoLocal = s.match(/^(\d{4})-(\d{2})-(\d{2})[T ]\d/);
  if (civilFromIsoLocal && !hasExplicitZone) {
    const y = Number(civilFromIsoLocal[1]);
    const mo = Number(civilFromIsoLocal[2]);
    const d = Number(civilFromIsoLocal[3]);
    const dt = new Date(y, mo - 1, d);
    return Number.isNaN(dt.getTime()) ? null : dt;
  }
  const dt = new Date(s);
  return Number.isNaN(dt.getTime()) ? null : dt;
}

function CallTakerDispatcherInsightColumn({
  callerPhone,
  locationText,
  coordinateX,
  coordinateY,
  locationSelected,
  geoFormValues,
  incidentFormValues,
  onCallOpenTimerChange,
}) {

  const [geoSidePanel, setGeoSidePanel] = useState([]);
  const { resourceData } = useContext(IncidentContext);

  const availableResources = resourceData?.filter((resource) => resource?.Status === 'AV');
  const trimmedLocation =
    locationText != null && String(locationText).trim() !== '' ? String(locationText).trim() : '';

  const getOtherZoneFireResourcesKey = `/CAD/GeoLocation/GetGeoSidePanelData/${geoFormValues?.Id}`;
  const { data: geoSidePanelData, isSuccess: isFetchGeoSidePanelData } =
    useQuery(
      [getOtherZoneFireResourcesKey, { ID: geoFormValues?.Id, }],
      CallTakerServices.getGeoSidePanelData,
      {
        refetchOnWindowFocus: false,
        enabled: !!geoFormValues?.Id
      }
    );
  useEffect(() => {
    if (isFetchGeoSidePanelData && geoSidePanelData) {
      const data = JSON.parse(geoSidePanelData?.data?.data);
      setGeoSidePanel(data);
    } else {
      setGeoSidePanel([]);
    }
  }, [isFetchGeoSidePanelData, geoSidePanelData])


  const address = trimmedLocation || '';
  const lat = coordinateY != null && String(coordinateY).trim() !== '' ? String(coordinateY).trim() : '';
  const lng = coordinateX != null && String(coordinateX).trim() !== '' ? String(coordinateX).trim() : '';


  const UnitDot = ({ tone }) => (
    <span
      aria-hidden
      style={{
        width: 7,
        height: 7,
        borderRadius: '50%',
        flexShrink: 0,
        background:
          tone === 'av' ? '#16a34a' : tone === 'busy' ? '#eab308' : '#dc2626',
      }}
    />
  );

  const UnitTag = ({ code, tone, fill }) => (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 5,
        padding: '3px 8px',
        borderRadius: 5,
        border: '1px solid #e2e8f0',
        background: fill === 'yellow' ? '#fef9c3' : fill === 'red' ? '#ffe4e6' : '#fff',
        ...insightMono,
        fontSize: 10,
        fontWeight: 700,
        color: '#0f172a',
      }}
    >
      <UnitDot tone={tone} />
      {code}
    </span>
  );

  const ResourceGroup = ({ title, sub, countLabel, children }) => (
    <div style={{ marginBottom: 10 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 6 }}>
        <span style={{ fontWeight: 800, fontSize: 11, color: '#14532d' }}>{title}</span>
        <span style={{ fontSize: 10, fontWeight: 700, color: '#166534' }}>{countLabel}</span>
      </div>
      {sub ? <div style={{ fontSize: 9, color: '#64748b', marginBottom: 6 }}>{sub}</div> : null}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>{children}</div>
    </div>
  );

  const disciplineGroups = useMemo(() => {
    const list = Array.isArray(availableResources) ? availableResources : [];
    const avCount = (items) =>
      items.filter((r) => String(r?.Status ?? '').trim().toUpperCase() === 'AV').length;
    return [
      { key: 'law', title: 'LAW', items: list.filter((r) => disciplineFlagTrue(r?.isLaw)) },
      { key: 'zone', title: 'ZONE', items: list.filter((r) => disciplineFlagTrue(r?.isFire)) },
      { key: 'other', title: 'OTHER', items: list.filter((r) => disciplineFlagTrue(r?.isOther)) },
      { key: 'ems', title: 'EMS', items: list.filter((r) => disciplineFlagTrue(r?.isEMS)) },
    ]
      .filter((g) => g.items.length > 0)
      .map((g) => ({ ...g, avCount: avCount(g.items) }));
  }, [availableResources]);

  return (
    <div
      className="cad-calltaker-insight-col"
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: 10,
        maxHeight: 640,
        overflowY: 'auto',
        paddingLeft: 4,
      }}
    >
      {/* ANI / ALI */}
      <div style={{ ...insightCard, background: '#e0f2fe', border: '1px solid #7dd3fc' }}>
        <div style={{ ...insightSectionTitle, marginBottom: 6, color: '#0c4a6e' }}>
          <span>ANI / ALI</span>
          <span
            style={{
              marginLeft: 'auto',
              fontSize: 9,
              fontWeight: 800,
              padding: '2px 8px',
              borderRadius: 999,
              background: '#22c55e',
              color: '#fff',
              letterSpacing: '0.04em',
            }}
          >
            ACTIVE
          </span>
        </div>
        {incidentFormValues?.FirePhoneNo && <div style={{ ...insightMono, fontWeight: 700, fontSize: 13, color: '#0f172a' }}>{incidentFormValues?.FirePhoneNo}</div>}
        {/* <div style={{ marginTop: 4, display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
          <span style={{ fontSize: 10, padding: '2px 6px', borderRadius: 4, background: '#bae6fd', color: '#075985' }}>
            Phase II Cell
          </span>
          <span style={{ fontSize: 10, color: '#475569' }}>T-Mobile</span>
        </div> */}
        {/* <div style={{ marginTop: 10 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 10, fontWeight: 600, color: '#0369a1' }}>
            <span>Confidence</span>
            <span>{confidencePct}%</span>
          </div>
          <div style={{ height: 8, borderRadius: 999, background: '#bae6fd', marginTop: 4, overflow: 'hidden' }}>
            <div style={{ width: `${confidencePct}%`, height: '100%', background: '#16a34a', borderRadius: 999 }} />
          </div>
        </div> */}
        <div style={{ marginTop: 10, fontSize: 11, color: '#0f172a', fontWeight: 600 }}>{address}</div>
        <div style={{ marginTop: 4, fontSize: 10, color: '#64748b', ...insightMono }}>
          Lat {lat} · Long {lng}
        </div>
      </div>

      {/* Call timer */}
      <CallOpenInsightCard
        trimmedLocation={trimmedLocation}
        locationSelected={locationSelected}
        onTimerTextChange={onCallOpenTimerChange}
      />

      {/* CFS protocol documents */}
      {geoSidePanel?.Table2?.length > 0 &&
        <CfsProtocolInsightCard documents={geoSidePanel?.Table2} />}

      {/* Available */}
      {disciplineGroups?.length > 0 && <div style={{ ...insightCard, background: '#ecfccb', border: '1px solid #bef264' }}>
        <div style={{ ...insightSectionTitle, color: '#365314' }}>
          <i className="fa fa-bullhorn" aria-hidden style={{ fontSize: 12 }} />
          <span>Available</span>
        </div>
        {disciplineGroups?.length > 0 && disciplineGroups.map((g) => (
          <ResourceGroup key={g.key} title={g.title} countLabel={`${g.avCount} AV`}>
            {g.items.map((r) => {
              const rowKey = r?.ResourceID ?? r?.ResourceNumber;
              const tone = statusToUnitTone(r?.Status);
              const isAv = String(r?.Status ?? '').trim().toUpperCase() === 'AV';
              return (
                <UnitTag
                  key={`${g.key}-${rowKey}`}
                  code={r?.ResourceNumber || r?.ResourceTypeCode || String(rowKey)}
                  tone={tone}
                  fill={isAv ? undefined : 'red'}
                />
              );
            })}
          </ResourceGroup>
        ))}
      </div>}

      {/* Address history */}
      {geoSidePanel?.Table1?.length > 0 && <div style={{ ...insightCard, background: '#fff', border: '1px solid #e2e8f0' }}>
        <div style={{ ...insightSectionTitle }}>
          <i className="fa fa-history" aria-hidden />
          <span>Address history</span>
          <span style={{ marginLeft: 'auto', fontSize: 10 }}>{geoSidePanel?.Table1?.length} calls</span>
        </div>
        {geoSidePanel?.Table1?.map((row) => (
          <div
            key={row.IncidentID}
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 8,
              padding: '6px 0',
              borderTop: '1px solid #f1f5f9',
              fontSize: 10,
            }}
          >
            <span style={{ color: '#2563eb', fontWeight: 700, ...insightMono }}>{row.CADIncidentNumber}</span>
            <span style={{ flex: 1, color: '#334155' }}>{row.CFSCodeDescription}</span>
            <span style={{ color: '#94a3b8', fontSize: 9 }}>{getShowingDateText(row?.CreatedDtTm)}</span>
            <span
              style={{
                fontSize: 9,
                fontWeight: 700,
                padding: '2px 6px',
                borderRadius: 4,
                textTransform: 'uppercase',
                background: !row.Disposition ? '#fecaca' : '#f1f5f9',
                color: !row.Disposition ? '#991b1b' : '#64748b',
              }}
            >
              {!row.Disposition ? 'Open' : 'Closed'}
            </span>
          </div>
        ))}
      </div>}

      {/* Nearby active */}
      {/* <div style={{ ...insightCard, background: '#ede9fe', border: '1px solid #c4b5fd' }}>
        <div style={{ ...insightSectionTitle, color: '#4c1d95' }}>
          <i className="fa fa-map-marker" aria-hidden />
          <span>Nearby active</span>
        </div>
        {demoNearby.map((row) => (
          <div
            key={row.id}
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 8,
              padding: '6px 0',
              borderTop: '1px solid #ddd6fe',
              fontSize: 10,
            }}
          >
            <span style={{ fontWeight: 700, ...insightMono, color: '#1e1b4b' }}>{row.id}</span>
            <span style={{ flex: 1, color: '#4338ca' }}>{row.label}</span>
            <span style={{ color: '#6d28d9', fontSize: 9 }}>{row.meta}</span>
            {row.active ? (
              <span style={{ fontSize: 9, fontWeight: 700, color: '#b91c1c' }}>active</span>
            ) : null}
          </div>
        ))}
      </div> */}

      {/* Premise contacts */}
      {geoSidePanel?.Table3?.length > 0 && <div style={{ ...insightCard, background: '#fff', border: '1px solid #e2e8f0' }}>
        <div style={{ ...insightSectionTitle }}>
          <i className="fa fa-phone" aria-hidden />
          <span>Premise contacts</span>
        </div>
        {geoSidePanel?.Table3?.map((c) => (
          <div
            key={c.phone}
            style={{
              padding: '6px 0',
              borderTop: '1px solid #f1f5f9',
              fontSize: 10,
            }}
          >
            <div style={{ fontWeight: 700, color: '#0f172a' }}>{c.FirstName + ' ' + c.LastName}</div>
            <div style={{ color: '#64748b', fontSize: 9 }}>{c.PhoneType}</div>
            <div style={{ ...insightMono, color: '#0369a1', marginTop: 2 }}>{c.PhoneNo}</div>
          </div>
        ))}
      </div>}
    </div>
  );
}

CallTakerDispatcherInsightColumn.propTypes = {
  callerPhone: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  locationText: PropTypes.string,
  coordinateX: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  coordinateY: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onCallOpenTimerChange: PropTypes.func,
};

export default CallTakerDispatcherInsightColumn;
